/**
 * Tâche 1444 (Eric 2026-10-02) — Synchroniser la position temporelle entre les
 * 3 transcriptions d'une vidéo.
 *
 * > « quand on circule dans une des 3 transcriptions d'une vidéo pour se rendre
 * >   à un temps donné (ex 20 min 23 s), puis qu'on passe à l'une des 2 autres,
 * >   elle doit être affichée au temps le plus proche possible du temps de
 * >   référence, car cela permet de comparer au mieux le contenu. »
 *
 * **L'alignement se fait PAR LE TEMPS, jamais par le numéro de tour** — acquis
 * 1398 : les diariseurs ne coupent pas aux mêmes endroits, empiler les tours
 * deux à deux décale tout dès le premier écart et montre un faux problème.
 *
 * Les positions sont lues DANS LE DOM (`data-temps` posé par les trois
 * sous-onglets) et non dans les tableaux de segments. Raison : chaque
 * sous-onglet filtre, replie et réordonne sa propre liste (filtre par
 * locuteur, filtre par provenance, paragraphes sans horodatage). Le DOM est la
 * seule source qui dit ce qui est RÉELLEMENT affiché — viser un segment absent
 * de l'écran ne déplacerait rien et paraîtrait cassé.
 */

export const CONTENEURS = {
  whisper: "transcript-container",
  fireflies: "fireflies-container",
  merged: "merged-container",
};

export const ATTR_TEMPS = "data-temps";

/** Le conteneur défilant du sous-onglet demandé, ou `null`. */
export function conteneurDe(sousOnglet, racine = document) {
  const id = CONTENEURS[sousOnglet];
  return id ? racine.querySelector(`[data-testid="${id}"]`) : null;
}

/** Les lignes horodatées réellement affichées, dans l'ordre du DOM. */
export function lignesHorodatees(conteneur) {
  if (!conteneur) return [];
  return Array.from(conteneur.querySelectorAll(`[${ATTR_TEMPS}]`))
    .map((el) => ({ el, temps: Number(el.getAttribute(ATTR_TEMPS)) }))
    .filter((l) => Number.isFinite(l.temps));
}

/**
 * Le temps du premier tour de parole VISIBLE en haut du conteneur.
 *
 * C'est ce que la personne regarde quand elle fait défiler — et non le tour
 * actif du lecteur, qui peut être à dix écrans de là.
 */
export function tempsEnHautDe(conteneur) {
  const lignes = lignesHorodatees(conteneur);
  if (!lignes.length) return null;
  const hautConteneur = conteneur.getBoundingClientRect().top;
  for (const ligne of lignes) {
    // 4 px de tolérance : une ligne à cheval sur le bord est déjà « en haut ».
    if (ligne.el.getBoundingClientRect().bottom >= hautConteneur + 4) {
      return ligne.temps;
    }
  }
  return lignes[lignes.length - 1].temps;
}

/**
 * La ligne à montrer pour un temps de référence.
 *
 * `contient` est vrai quand le temps tombe DANS l'intervalle du tour, c'est-à-
 * dire entre son début et le début du suivant. Le dernier tour contient tout
 * ce qui le suit : sa fin n'est pas dans le DOM, et l'inventer ferait annoncer
 * un écart qu'on ne sait pas mesurer.
 */
export function ligneCible(lignes, temps) {
  if (!lignes.length || !Number.isFinite(temps)) return null;
  const triees = [...lignes].sort((a, b) => a.temps - b.temps);
  if (temps < triees[0].temps) {
    return { ...triees[0], contient: false, ecart: triees[0].temps - temps,
             position: "avant_le_debut" };
  }
  for (let i = triees.length - 1; i >= 0; i -= 1) {
    if (temps >= triees[i].temps) {
      const suivant = triees[i + 1];
      const contient = !suivant || temps < suivant.temps;
      return {
        ...triees[i],
        contient,
        ecart: temps - triees[i].temps,
        position: i === triees.length - 1 && !suivant ? "dans_le_dernier" : "dans_un_tour",
      };
    }
  }
  return null;
}

/** Amène la ligne en haut du conteneur, sans toucher au lecteur vidéo. */
export function amenerEnHaut(conteneur, el) {
  if (!conteneur || !el) return;
  const decalage = el.getBoundingClientRect().top
    - conteneur.getBoundingClientRect().top;
  conteneur.scrollTop += decalage;
}

export const CLASSE_REPERE = "sync-1444-repere";

/** mm:ss, ou h:mm:ss au-delà d'une heure. */
export function formaterTemps(secondes) {
  const s = Math.max(0, Math.round(Number(secondes) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const deux = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${deux(m)}:${deux(r)}` : `${m}:${deux(r)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tâche 1453 (Eric 2026-06) — Le réglage « curseurs synchronisés ».
// ─────────────────────────────────────────────────────────────────────────────
// > « remplaces le bouton de synchronisation des transcriptions par une case à
// >   cocher suivie du texte "curseurs synchronisés", que tu vas placer sous le
// >   menu déroulant permettant de choisir 1 des 3 transcriptions »
//
// ⚠️ La case VIT DANS LA BARRE D'OUTILS et le bandeau d'état vit sous la barre :
// deux endroits, donc **un seul réglage partagé**. Deux `useState` séparés
// liraient chacun le même `localStorage` au montage puis divergeraient au
// premier clic — la case dirait « coché » pendant que la synchronisation
// resterait éteinte.
export const CLE_REGLAGE_SYNC = "knoria.sync_curseurs_transcriptions";

export function lireReglageSync() {
  try {
    // Allumé par défaut (choix d'Eric en 1444) : seule la valeur « 0 »
    // explicitement écrite éteint la synchronisation.
    return window.localStorage.getItem(CLE_REGLAGE_SYNC) !== "0";
  } catch {
    return true;
  }
}

export function ecrireReglageSync(actif) {
  try {
    window.localStorage.setItem(CLE_REGLAGE_SYNC, actif ? "1" : "0");
  } catch { /* navigation privée : le réglage vaut pour la session */ }
}
